import React, { Component } from 'react'
import {
    Card, Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Space,
    Popconfirm, message, Typography, Badge, Drawer, Row, Col, Alert,
    Descriptions, Empty, Spin, List,
} from 'antd'
import {
    PlusOutlined, EditOutlined, StopOutlined, ReloadOutlined,
    CodeOutlined, EyeOutlined, LinkOutlined, RocketOutlined, ArrowRightOutlined,
} from '@ant-design/icons'
import {
    getApiRegistryList,
    saveApiRegistry,
    updateApiRegistry,
    deleteApiRegistry,
    getApiRegistryImpact,
} from '../../request'

const { Text } = Typography
const { TextArea } = Input

const METHOD_COLOR = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    DELETE: 'red',
    PATCH: 'purple',
}

const STATUS_OPTIONS = [
    { label: '启用', value: 'enabled' },
    { label: '停用', value: 'disabled' },
]

const IMPACT_COLOR = {
    high: 'red',
    medium: 'orange',
    low: 'blue',
}

export default class McpApiRegistry extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        loading: false,
        submitting: false,
        modalVisible: false,
        schemaDrawerVisible: false,
        impactLoading: false,
        editRecord: null,
        schemaRecord: null,
        impactDetail: null,
        filterMethod: undefined,
        filterKeyword: '',
        filterStatus: undefined,
    }

    componentDidMount() {
        this.loadList()
    }

    normalizeRecord = (record = {}) => ({
        ...record,
        version: record.version || record.apiVersion,
        status: record.status || 'enabled',
        usedByToolsCount: record.usedByToolsCount ?? 0,
    })

    isEnabled = (status) => status === 'enabled' || status === 1

    getStatusText = (status) => (this.isEnabled(status) ? '启用' : '停用')

    safeParseJson = (value) => {
        if (!value) return null
        try {
            return JSON.parse(value)
        } catch (error) {
            return value
        }
    }

    safeFormatJson = (value) => {
        if (!value) return ''
        try {
            return JSON.stringify(JSON.parse(value), null, 2)
        } catch (error) {
            return value
        }
    }

    loadList = () => {
        this.setState({ loading: true })
        getApiRegistryList({})
            .then((res) => {
                const list = Array.isArray(res) ? res : (res?.list || [])
                this.setState({ dataSource: list.map(this.normalizeRecord) })
            })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    openCreate = () => {
        this.setState({ modalVisible: true, editRecord: null })
        setTimeout(() => {
            this.formRef.current?.resetFields()
            this.formRef.current?.setFieldsValue({
                method: 'POST',
                status: 'enabled',
                version: '1.0.0',
            })
        }, 0)
    }

    openEdit = (record) => {
        const normalized = this.normalizeRecord(record)
        this.setState({ modalVisible: true, editRecord: normalized })
        setTimeout(() => {
            this.formRef.current?.setFieldsValue({
                apiCode: normalized.apiCode,
                apiName: normalized.apiName,
                method: normalized.method,
                path: normalized.path,
                baseUrl: normalized.baseUrl,
                sourceSystem: normalized.sourceSystem,
                authType: normalized.authType,
                timeoutMs: normalized.timeoutMs,
                owner: normalized.owner,
                description: normalized.description,
                version: normalized.version,
                status: normalized.status,
                requestSchema: this.safeFormatJson(normalized.requestSchema),
                responseSchema: this.safeFormatJson(normalized.responseSchema),
            })
        }, 0)
    }

    openDetail = (record) => {
        const normalized = this.normalizeRecord(record)
        this.setState({
            schemaDrawerVisible: true,
            schemaRecord: normalized,
            impactDetail: null,
            impactLoading: true,
        })
        getApiRegistryImpact({ id: normalized.id })
            .then((res) => this.setState({ impactDetail: res || null }))
            .catch(() => this.setState({ impactDetail: null }))
            .finally(() => this.setState({ impactLoading: false }))
    }

    handleSubmit = (values) => {
        const { editRecord } = this.state

        for (const field of ['requestSchema', 'responseSchema']) {
            if (!values[field]) continue
            try {
                JSON.parse(values[field])
            } catch (error) {
                message.error(`${field === 'requestSchema' ? '请求' : '响应'} Schema 不是合法 JSON`)
                return
            }
        }

        const payload = {
            ...values,
            status: values.status || 'enabled',
        }

        this.setState({ submitting: true })
        const request = editRecord
            ? updateApiRegistry({ ...payload, id: editRecord.id })
            : saveApiRegistry(payload)

        request.then(() => {
            message.success(editRecord ? '接口已更新' : '接口已注册')
            this.setState({ modalVisible: false, editRecord: null })
            this.loadList()
        }).catch(() => {})
            .finally(() => this.setState({ submitting: false }))
    }

    handleDelete = (record) => {
        deleteApiRegistry({ id: record.id })
            .then(() => {
                message.success('接口已停用')
                this.loadList()
            })
            .catch(() => {})
    }

    getFilteredData = () => {
        const { dataSource, filterMethod, filterKeyword, filterStatus } = this.state
        return dataSource.filter((item) => {
            if (filterMethod && item.method !== filterMethod) return false
            if (filterStatus && item.status !== filterStatus) return false
            if (!filterKeyword) return true
            const keyword = filterKeyword.toLowerCase()
            return (item.apiCode || '').toLowerCase().includes(keyword)
                || (item.apiName || '').toLowerCase().includes(keyword)
                || (item.path || '').toLowerCase().includes(keyword)
                || (item.description || '').toLowerCase().includes(keyword)
        })
    }

    clearFilters = () => {
        this.setState({
            filterMethod: undefined,
            filterKeyword: '',
            filterStatus: undefined,
        })
    }

    goToToolWorkbench = (toolId) => {
        const query = toolId ? `?toolId=${encodeURIComponent(toolId)}` : ''
        this.props.history.push(`/admin/mcptool${query}`)
    }

    goToRelatedTools = (apiCode) => {
        this.props.history.push(`/admin/mcptool?apiCode=${encodeURIComponent(apiCode)}`)
    }

    renderStatusTag = (status) => (
        <Tag color={this.isEnabled(status) ? 'success' : 'default'}>
            {this.getStatusText(status)}
        </Tag>
    )

    renderHero = () => (
        <div style={{
            marginBottom: 16,
            padding: '22px 26px',
            borderRadius: 18,
            background: 'linear-gradient(135deg, #f8fbff 0%, #eef6ff 60%, #f2fffc 100%)',
            border: '1px solid #dbe7ff',
        }}>
            <Row gutter={[24, 24]} align="middle">
                <Col xs={24} lg={16}>
                    <div style={{ fontSize: 14, color: '#175ec9', fontWeight: 600, marginBottom: 8 }}>API Registry</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#101828', marginBottom: 8 }}>
                        接口资产中心负责维护事实，影响分析负责告诉你哪里会受波动
                    </div>
                    <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.8 }}>
                        这里维护 method、path、schema 和版本这些可复用接口事实。Tool 的语义包装、参数暴露和发布动作仍然留在工具工作台完成。
                    </Text>
                </Col>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        <Button type="primary" size="large" icon={<RocketOutlined />} onClick={() => this.goToToolWorkbench()}>
                            进入工具工作台
                        </Button>
                        <Button size="large" icon={<PlusOutlined />} onClick={this.openCreate}>
                            注册接口
                        </Button>
                    </Space>
                </Col>
            </Row>
        </div>
    )

    renderStats = (dataSource) => {
        const summary = dataSource.reduce((acc, item) => {
            acc.total += 1
            if (this.isEnabled(item.status)) acc.active += 1
            if (item.requestSchema) acc.request += 1
            if ((item.usedByToolsCount ?? 0) > 0) acc.referenced += 1
            return acc
        }, { total: 0, active: 0, request: 0, referenced: 0 })

        const cards = [
            { title: '接口总数', value: summary.total, color: '#175ec9', icon: <LinkOutlined /> },
            { title: '启用接口', value: summary.active, color: '#389e0d', icon: <ArrowRightOutlined /> },
            { title: '已补请求 Schema', value: summary.request, color: '#d48806', icon: <CodeOutlined /> },
            { title: '已被工具引用', value: summary.referenced, color: '#08979c', icon: <EyeOutlined /> },
        ]

        return (
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                {cards.map((card) => (
                    <Col xs={24} sm={12} xl={6} key={card.title}>
                        <Card bordered={false} style={{
                            borderRadius: 16,
                            border: `1px solid ${card.color}22`,
                            background: `linear-gradient(180deg, ${card.color}12 0%, #ffffff 70%)`,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 12,
                                    background: `${card.color}18`,
                                    color: card.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                }}>
                                    {card.icon}
                                </div>
                                <div>
                                    <div style={{ fontSize: 12, color: '#667085', marginBottom: 4 }}>{card.title}</div>
                                    <div style={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>
        )
    }

    renderSchemaPanel = (title, schemaValue, color) => {
        const parsed = this.safeParseJson(schemaValue)
        if (!schemaValue) {
            return (
                <div style={{
                    padding: 18,
                    borderRadius: 14,
                    border: '1px dashed #d0d5dd',
                    background: '#fff',
                }}>
                    <Text type="secondary">暂无 {title}</Text>
                </div>
            )
        }

        return (
            <div>
                <div style={{ marginBottom: 8 }}>
                    <Tag color={color}>{title}</Tag>
                </div>
                <pre style={{
                    margin: 0,
                    padding: 16,
                    background: '#0f172a',
                    color: '#e2e8f0',
                    borderRadius: 14,
                    fontSize: 12,
                    lineHeight: 1.7,
                    overflow: 'auto',
                    maxHeight: 340,
                    border: '1px solid #1e293b',
                }}>
                    {typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}
                </pre>
            </div>
        )
    }

    renderImpactPanel = () => {
        const { impactLoading, impactDetail, schemaRecord } = this.state
        if (impactLoading) {
            return (
                <div style={{ padding: '24px 0', textAlign: 'center' }}>
                    <Spin />
                </div>
            )
        }

        const affectedTools = impactDetail?.affectedTools || []
        if (affectedTools.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={schemaRecord?.usedByToolsCount > 0 ? '当前没有拿到工具影响明细' : '当前接口尚未被工具引用'}
                />
            )
        }

        return (
            <List
                dataSource={affectedTools}
                renderItem={(item) => (
                    <List.Item
                        key={item.toolId || item.toolCode}
                        style={{ padding: '14px 0' }}
                        actions={[
                            <Button
                                key="open"
                                size="small"
                                type="primary"
                                ghost
                                icon={<ArrowRightOutlined />}
                                onClick={() => this.goToToolWorkbench(item.toolId)}
                            >
                                查看工具
                            </Button>,
                        ]}
                    >
                        <List.Item.Meta
                            title={(
                                <Space wrap>
                                    <Text strong>{item.toolName || item.toolCode}</Text>
                                    <Text code>{item.toolCode}</Text>
                                    {item.impactLevel ? (
                                        <Tag color={IMPACT_COLOR[item.impactLevel] || 'default'}>
                                            {item.impactLevel.toUpperCase()}
                                        </Tag>
                                    ) : null}
                                </Space>
                            )}
                            description={(
                                <Space wrap>
                                    {(item.failedMappings || []).length > 0 ? (
                                        item.failedMappings.map((mapping) => (
                                            <Tag key={`${item.toolId}-${mapping}`} color="orange">
                                                {mapping}
                                            </Tag>
                                        ))
                                    ) : (
                                        <Text type="secondary">暂未返回失败映射项</Text>
                                    )}
                                </Space>
                            )}
                        />
                    </List.Item>
                )}
            />
        )
    }

    buildColumns = () => [
        {
            title: '接口',
            key: 'api',
            width: 320,
            render: (_, record) => (
                <div>
                    <Space size={8} style={{ marginBottom: 6, flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: 14 }}>{record.apiName || '-'}</Text>
                        <Text code style={{ fontSize: 11 }}>{record.apiCode}</Text>
                        {record.version ? <Badge color="geekblue" text={`v${record.version}`} /> : null}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.description || '建议补充接口用途和边界说明'}
                    </Text>
                </div>
            ),
        },
        {
            title: '请求信息',
            key: 'request',
            width: 280,
            render: (_, record) => (
                <div>
                    <Space size={8} style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                        <Tag color={METHOD_COLOR[record.method] || 'default'} style={{ fontWeight: 700, letterSpacing: 1 }}>
                            {record.method || '-'}
                        </Tag>
                        {this.renderStatusTag(record.status)}
                    </Space>
                    <div style={{ marginBottom: 4 }}>
                        <Text code copyable style={{ fontSize: 12 }}>{record.path || '-'}</Text>
                    </div>
                    <div>
                        {record.baseUrl
                            ? <Text type="secondary" style={{ fontSize: 11 }}>{record.baseUrl}</Text>
                            : <Text type="danger" style={{ fontSize: 11 }}>base_url 未配置</Text>
                        }
                    </div>
                </div>
            ),
        },
        {
            title: 'Schema',
            key: 'schema',
            width: 180,
            render: (_, record) => (
                <Space size={8} wrap>
                    <Badge status={record.requestSchema ? 'processing' : 'default'} text="请求" />
                    <Badge status={record.responseSchema ? 'success' : 'default'} text="响应" />
                </Space>
            ),
        },
        {
            title: '引用情况',
            key: 'relation',
            width: 180,
            render: (_, record) => (
                <div>
                    <Space size={8} style={{ marginBottom: 6 }}>
                        <Badge
                            count={record.usedByToolsCount || 0}
                            showZero
                            style={{ backgroundColor: record.usedByToolsCount ? '#175ec9' : '#d9d9d9' }}
                        />
                        <Text type="secondary">关联工具</Text>
                    </Space>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.usedByToolsCount > 0 ? '可查看真实影响分析' : '当前尚未被工具消费'}
                        </Text>
                    </div>
                </div>
            ),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 320,
            render: (_, record) => (
                <Space size={6} wrap>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => this.openDetail(record)}>
                        查看详情
                    </Button>
                    <Button
                        size="small"
                        type="primary"
                        ghost
                        icon={<ArrowRightOutlined />}
                        disabled={!record.usedByToolsCount}
                        onClick={() => this.goToRelatedTools(record.apiCode)}
                    >
                        查看工具
                    </Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => this.openEdit(record)}>
                        编辑
                    </Button>
                    <Popconfirm title="确认停用这个接口？" onConfirm={() => this.handleDelete(record)}>
                        <Button size="small" danger icon={<StopOutlined />}>
                            停用
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    render() {
        const {
            loading,
            submitting,
            modalVisible,
            schemaDrawerVisible,
            editRecord,
            schemaRecord,
            filterMethod,
            filterKeyword,
            filterStatus,
            dataSource,
        } = this.state

        const filteredData = this.getFilteredData()

        return (
            <div style={{ padding: '0 4px' }}>
                {this.renderHero()}
                {this.renderStats(dataSource)}

                <Card
                    bordered={false}
                    style={{ borderRadius: 18, border: '1px solid #edf1f7' }}
                    title={(
                        <Space>
                            <LinkOutlined style={{ color: '#175ec9' }} />
                            <span style={{ fontWeight: 600 }}>接口资产中心</span>
                            <Text type="secondary" style={{ fontSize: 12 }}>共 {filteredData.length} 条接口资产</Text>
                        </Space>
                    )}
                    extra={(
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>注册接口</Button>
                        </Space>
                    )}
                >
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12, borderRadius: 12 }}
                        message="接口中心只维护事实，不承载工具语义包装。"
                        description="Tool 的参数暴露、输出结构、Mapping 和发布节奏请在工具工作台中完成。接口中心重点是版本、Schema 和影响分析。"
                    />

                    <div style={{
                        display: 'flex',
                        gap: 8,
                        marginBottom: 16,
                        flexWrap: 'wrap',
                        padding: 14,
                        borderRadius: 14,
                        background: '#f8fafc',
                    }}>
                        <Select
                            style={{ width: 140 }}
                            placeholder="请求方法"
                            allowClear
                            value={filterMethod}
                            onChange={(value) => this.setState({ filterMethod: value })}
                            options={['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((method) => ({
                                label: <Tag color={METHOD_COLOR[method]}>{method}</Tag>,
                                value: method,
                            }))}
                        />
                        <Select
                            style={{ width: 120 }}
                            placeholder="状态"
                            allowClear
                            value={filterStatus}
                            onChange={(value) => this.setState({ filterStatus: value })}
                            options={STATUS_OPTIONS}
                        />
                        <Input.Search
                            style={{ width: 280 }}
                            placeholder="搜索编码、名称、路径、描述"
                            value={filterKeyword}
                            allowClear
                            onChange={(e) => this.setState({ filterKeyword: e.target.value })}
                        />
                        <Button onClick={this.clearFilters}>清空筛选</Button>
                    </div>

                    <Table
                        bordered
                        size="middle"
                        rowKey="id"
                        dataSource={filteredData}
                        columns={this.buildColumns()}
                        loading={loading}
                        scroll={{ x: 1180 }}
                        pagination={{ size: 'small', pageSize: 20, showSizeChanger: true }}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前筛选下没有接口资产，可先注册一个接口" /> }}
                    />
                </Card>

                <Modal
                    title={(
                        <Space>
                            {editRecord ? <EditOutlined /> : <PlusOutlined />}
                            {editRecord ? '编辑接口' : '注册新接口'}
                        </Space>
                    )}
                    visible={modalVisible}
                    onCancel={() => this.setState({ modalVisible: false, editRecord: null })}
                    footer={null}
                    destroyOnClose
                    width={760}
                >
                    <div style={{
                        marginBottom: 16,
                        padding: 12,
                        borderRadius: 12,
                        background: '#f8fafc',
                        border: '1px solid #edf1f7',
                    }}>
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.7 }}>
                            这里注册的是可复用 API 资产。真正暴露给模型的语义、示例和 Mapping，将在工具工作台中继续完善。
                        </Text>
                    </div>
                    <Form
                        ref={this.formRef}
                        layout="vertical"
                        onFinish={this.handleSubmit}
                        initialValues={{ method: 'POST', status: 'enabled', version: '1.0.0' }}
                    >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                            <Form.Item name="apiCode" label="接口编码" rules={[{ required: true, message: '请输入接口编码' }]}>
                                <Input placeholder="例如：query_sales_data" disabled={!!editRecord} />
                            </Form.Item>
                            <Form.Item name="apiName" label="接口名称" rules={[{ required: true, message: '请输入接口名称' }]}>
                                <Input placeholder="例如：查询销售数据" />
                            </Form.Item>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px', gap: '0 16px' }}>
                            <Form.Item name="method" label="请求方法" rules={[{ required: true }]}>
                                <Select options={['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((method) => ({ label: method, value: method }))} />
                            </Form.Item>
                            <Form.Item name="path" label="接口路径" rules={[{ required: true, message: '请输入接口路径' }]}>
                                <Input placeholder="/api/v1/sales/query" />
                            </Form.Item>
                            <Form.Item name="version" label="接口版本">
                                <Input placeholder="1.0.0" />
                            </Form.Item>
                        </div>
                        <Form.Item
                            name="baseUrl"
                            label={<Space><span>Base URL</span><Text type="secondary" style={{ fontSize: 11 }}>网关路由前缀，不能为空</Text></Space>}
                            rules={[{ required: true, message: '请输入 Base URL，下游网关依赖此字段路由调用' }]}
                        >
                            <Input placeholder="https://gatewaytest.lechun.cc/lechun-bi" />
                        </Form.Item>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                            <Form.Item name="sourceSystem" label="来源系统">
                                <Input placeholder="例如：lechun-bi" />
                            </Form.Item>
                            <Form.Item name="authType" label="认证方式">
                                <Select
                                    allowClear
                                    placeholder="选择认证方式"
                                    options={[
                                        { label: '无认证', value: 'none' },
                                        { label: 'Session', value: 'session' },
                                        { label: 'API Key', value: 'api_key' },
                                        { label: 'OAuth2', value: 'oauth2' },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item name="timeoutMs" label="超时(ms)">
                                <InputNumber style={{ width: '100%' }} min={1000} max={60000} step={1000} placeholder="10000" />
                            </Form.Item>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                            <Form.Item name="description" label="描述">
                                <Input placeholder="接口职责、边界和注意事项" />
                            </Form.Item>
                            <Form.Item name="owner" label="负责人">
                                <Input placeholder="接口负责人" />
                            </Form.Item>
                        </div>
                        <Form.Item name="status" label="状态">
                            <Select options={STATUS_OPTIONS} style={{ width: 140 }} />
                        </Form.Item>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                            <Form.Item
                                name="requestSchema"
                                label={<Space><span>请求 Schema</span><Text type="secondary" style={{ fontSize: 11 }}>(JSON)</Text></Space>}
                            >
                                <TextArea rows={8} placeholder='{"type":"object","properties":{"date":{"type":"string"}}}' style={{ fontFamily: 'monospace', fontSize: 12 }} />
                            </Form.Item>
                            <Form.Item
                                name="responseSchema"
                                label={<Space><span>响应 Schema</span><Text type="secondary" style={{ fontSize: 11 }}>(JSON)</Text></Space>}
                            >
                                <TextArea rows={8} placeholder='{"type":"object","properties":{"data":{"type":"array"}}}' style={{ fontFamily: 'monospace', fontSize: 12 }} />
                            </Form.Item>
                        </div>
                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => this.setState({ modalVisible: false, editRecord: null })}>取消</Button>
                                <Button type="primary" htmlType="submit" loading={submitting}>
                                    {editRecord ? '保存' : '注册'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>

                <Drawer
                    title={(
                        <Space>
                            <CodeOutlined />
                            <span>{schemaRecord?.apiName || '接口详情'}</span>
                        </Space>
                    )}
                    visible={schemaDrawerVisible}
                    onClose={() => this.setState({ schemaDrawerVisible: false, schemaRecord: null, impactDetail: null })}
                    width={760}
                    bodyStyle={{ padding: 20, background: '#f5f7fb' }}
                >
                    {schemaRecord && (
                        <div>
                            {schemaRecord.usedByToolsCount > 0 ? (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16, borderRadius: 12 }}
                                    message={`当前接口已被 ${schemaRecord.usedByToolsCount} 个工具引用`}
                                    description="请结合下方影响分析确认本次接口调整会影响哪些工具和哪些映射。"
                                    action={
                                        <Button size="small" onClick={() => this.goToRelatedTools(schemaRecord.apiCode)}>
                                            查看工具列表
                                        </Button>
                                    }
                                />
                            ) : null}

                            <Card bordered={false} style={{ borderRadius: 16, marginBottom: 16, border: '1px solid #edf1f7' }}>
                                <Descriptions column={2} size="small" colon={false}>
                                    <Descriptions.Item label="接口名称">{schemaRecord.apiName || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="接口编码">{schemaRecord.apiCode || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="请求方法">
                                        <Tag color={METHOD_COLOR[schemaRecord.method] || 'default'}>{schemaRecord.method || '-'}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="状态">{this.renderStatusTag(schemaRecord.status)}</Descriptions.Item>
                                    <Descriptions.Item label="接口路径">
                                        <Text code>{schemaRecord.path || '-'}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="接口版本">
                                        {schemaRecord.version ? `v${schemaRecord.version}` : '-'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="引用工具数">
                                        {schemaRecord.usedByToolsCount || 0}
                                    </Descriptions.Item>
                                </Descriptions>
                                <div style={{ marginTop: 12 }}>
                                    <Text type="secondary">{schemaRecord.description || '暂无描述'}</Text>
                                </div>
                            </Card>

                            <Card bordered={false} style={{ borderRadius: 16, marginBottom: 16, border: '1px solid #edf1f7' }}>
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 18, fontWeight: 600, color: '#101828', marginBottom: 6 }}>影响分析</div>
                                    <Text type="secondary">真实影响面来自后端分析结果，不再依赖前端自己推断引用关系。</Text>
                                </div>
                                {this.renderImpactPanel()}
                            </Card>

                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    {this.renderSchemaPanel('请求 Schema', schemaRecord.requestSchema, 'processing')}
                                </Col>
                                <Col span={24}>
                                    {this.renderSchemaPanel('响应 Schema', schemaRecord.responseSchema, 'success')}
                                </Col>
                            </Row>
                        </div>
                    )}
                </Drawer>
            </div>
        )
    }
}
